import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { QRCodeSVG } from "qrcode.react";
import { Link, useHistory } from "react-router-dom";
import { Button, Loader, Checkbox } from "semantic-ui-react";
import {
  FormBuilder,
  Form,
  ErrorMessage,
  authenticate,
} from "@redriver/cinnamon";
import { AppRoutes, SystemRoutes } from "constants/routes";
import { requestRegistration, completeRegistration } from "./actions";
import RecoveryCodes from "./RecoveryCodes";

const DEFAULT_MFA_RECOVERY_TYPE = "RecoveryCodes";

// Saffron returns this specific code as an HTTP 500 for an incorrect TOTP code
// at registration time (not a 4xx) - special-cased so it doesn't read as a
// generic server error.
const authenticatorErrors = {
  922001: "Incorrect authenticator code, please try again",
};

const authenticatorQrCode = (secret, label) =>
  `otpauth://totp/${encodeURIComponent(label)}?secret=${encodeURIComponent(
    secret
  )}`;

const AuthenticatorSetup = ({
  token,
  setupRecovery,
  email,
  loginTokens,
  enforceMfa,
  onSkipped,
}) => {
  const [setupFailed, setSetupFailed] = useState(false);
  const [setupSuccess, setSetupSuccess] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [codesSaved, setCodesSaved] = useState(false);
  const [postSetupTokens, setPostSetupTokens] = useState(null);
  const dispatch = useDispatch();
  const history = useHistory();
  const label = email ? `Maples Teesdale: ${email}` : "Maples Teesdale";

  const skipSetup = () => {
    // enforceMfa is checked here too, not just in whether the button renders below -
    // this function is the one thing that actually logs the user in without completing
    // MFA, so it shouldn't trust its caller alone to have kept it hidden
    if (!loginTokens || enforceMfa) return;
    const { access, refresh } = loginTokens;
    dispatch(authenticate(access, refresh));
    if (onSkipped) onSkipped();
  };

  if (setupFailed) {
    return (
      <div>
        <p>Multi-factor authentication setup failed.</p>
        <p>
          Return to <Link to={SystemRoutes.Login}>Log In</Link>
        </p>
      </div>
    );
  }

  if (setupSuccess) {
    const hasCodes = recoveryCodes?.length > 0;
    return (
      <div>
        <p>Your account has been successfully set up.</p>
        {hasCodes && <RecoveryCodes codes={recoveryCodes} />}
        <div style={{ marginTop: 20 }}>
          {hasCodes && (
            <Checkbox
              label="I have saved my recovery codes"
              checked={codesSaved}
              onChange={() => setCodesSaved(!codesSaved)}
              style={{ marginBottom: "1rem" }}
            />
          )}
          <Button
            primary
            fluid
            size="large"
            disabled={hasCodes && !codesSaved}
            onClick={() => {
              if (postSetupTokens) {
                const { access, refresh } = postSetupTokens;
                dispatch(authenticate(access, refresh));
              }
              history.replace(AppRoutes.Root);
            }}
          >
            Continue
          </Button>
        </div>
      </div>
    );
  }

  return (
    <FormBuilder
      loadAction={requestRegistration}
      loadParams={token}
      onLoaded={(response) => {
        if (!response?.secret) setSetupFailed(true);
      }}
      submitAction={completeRegistration}
      submitParams={{
        token,
        recovery: setupRecovery ? DEFAULT_MFA_RECOVERY_TYPE : undefined,
      }}
      onSubmitted={({ codes, tokens }) => {
        if (codes) setRecoveryCodes(codes);
        setPostSetupTokens(tokens);
        setSetupSuccess(true);
      }}
      renderLoading={false}
      renderSubmitting={false}
      renderError={false}
      renderForm={(
        formProps,
        { loading, slowLoading, submitting, slowSubmitting, error },
        events
      ) => {
        const { secret } = formProps.value;
        if (loading && slowLoading) {
          return <Loader inline active size="large" />;
        }
        if (!secret) return null;

        return (
          <Form {...formProps}>
            <p>
              This account requires multi-factor authentication. Download an
              authenticator app, scan the QR code below and enter the code it
              shows:
            </p>
            <div style={{ textAlign: "center" }}>
              <QRCodeSVG
                value={authenticatorQrCode(secret, label)}
                size={256}
              />
            </div>
            <p>
              Alternatively, manually enter the following key into your chosen
              authenticator app:
              <br />
              <strong>{secret}</strong>
            </p>
            <Form.Input
              field="totp"
              label="Authentication Code"
              placeholder="Enter authenticator app code"
              disabled={submitting}
              autoFocus
              required
              fluid
            />
            <Button
              primary
              onClick={events.onSubmit}
              disabled={submitting}
              loading={submitting && slowSubmitting}
              fluid
              size="large"
            >
              Continue
            </Button>
            {loginTokens && !enforceMfa && (
              <p>
                <button
                  type="button"
                  className="link-button"
                  onClick={skipSetup}
                >
                  Skip Multi-Factor Authentication
                </button>
              </p>
            )}
            <ErrorMessage
              header="Authentication Error"
              error={error}
              overrideMessages={authenticatorErrors}
              omitCodes
            />
            <p>
              Return to <Link to={SystemRoutes.Login}>Log In</Link>
            </p>
          </Form>
        );
      }}
    />
  );
};

export default AuthenticatorSetup;
